import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import metaAdAuditService from '../services/metaAdAudit.service';
// ══════════════════════════════════════════════════════════════════════════
// THUNKS
// ══════════════════════════════════════════════════════════════════════════
export const fetchReports = createAsyncThunk('metaAdAudit/fetchReports', async (params = {}, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await metaAdAuditService.listReports(params, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || 'Failed to fetch reports');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to fetch reports');
    }
});
export const createReportThunk = createAsyncThunk('metaAdAudit/createReport', async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await metaAdAuditService.createReport(payload, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(body?.message || 'Failed to create report');
        }
        // Backend returns 200 (sync) or 202 (queued)
        const queued = body?.statusCode === 202;
        // 202 response shape: { report_id, job_id, row_count, placeholder }
        // 200 response shape: the full saved report doc
        const report = queued ? body.data.placeholder : body.data;
        return {
            report,
            queued,
            message: body.message,
        };
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to create report');
    }
});
export const updateReportThunk = createAsyncThunk('metaAdAudit/updateReport', async ({ reportId, payload }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await metaAdAuditService.updateReport(reportId, payload, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(body?.message || 'Failed to update report');
        }
        const queued = body?.statusCode === 202;
        // 202 for update doesn't return the full report immediately;
        // we'll need the list to refetch for it to reappear cleanly.
        const report = queued ? null : body.data;
        return {
            report,
            queued,
            message: body.message,
        };
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to update report');
    }
});
export const refreshReportThunk = createAsyncThunk('metaAdAudit/refreshReport', async (reportId, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await metaAdAuditService.refreshReport(reportId, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(body?.message || 'Failed to refresh report');
        }
        const queued = body?.statusCode === 202;
        return {
            report: queued ? null : body.data,
            queued,
            message: body.message,
            reportId,
        };
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to refresh report');
    }
});
export const deleteReportThunk = createAsyncThunk('metaAdAudit/deleteReport', async (reportId, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await metaAdAuditService.deleteReport(reportId, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(body?.message || 'Failed to delete report');
        }
        return { reportId, message: body.message };
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to delete report');
    }
});
export const fetchJobStatus = createAsyncThunk('metaAdAudit/fetchJobStatus', async (reportId, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await metaAdAuditService.getJobStatus(reportId, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(body?.message || 'Failed to fetch job status');
        }
        return body.data;
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to fetch job status');
    }
});
export const fetchVisibleClients = createAsyncThunk('metaAdAudit/fetchVisibleClients', async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await metaAdAuditService.getVisibleClients(token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(body?.message || 'Failed to fetch clients');
        }
        // Only return visible ones
        const clients = body.data || [];
        return clients.filter((c) => c.visible !== false);
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to fetch clients');
    }
});
export const fetchClientsWithReports = createAsyncThunk('metaAdAudit/fetchClientsWithReports', async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await metaAdAuditService.listClientsWithReports(token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(body?.message || 'Failed to fetch clients');
        }
        return body.data || [];
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to fetch clients');
    }
});
export async function downloadDeckBlob(reportId, clientName, token) {
    const response = await metaAdAuditService.downloadDeck(reportId, token || '');
    if (!response || response.status !== 200) {
        const msg = response?.response?.data?.message ||
            response?.data?.message ||
            'Failed to download deck';
        throw new Error(msg);
    }
    const blob = response.data;
    const safeName = clientName.replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_');
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}_meta_ad_audit.pptx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}
export const fetchReportById = createAsyncThunk('metaAdAudit/fetchReportById', async (reportId, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await metaAdAuditService.getReportById(reportId, token || '');
        // ApiCall returns AxiosError on HTTP failures instead of throwing
        if (response?.isAxiosError || response instanceof Error) {
            const errData = response?.response?.data;
            return rejectWithValue(errData?.message || response?.message || 'Failed to load report');
        }
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(body?.message || 'Failed to load report');
        }
        return body.data;
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to load report');
    }
});
export const refreshCurrentReport = createAsyncThunk('metaAdAudit/refreshCurrentReport', async (reportId, { dispatch, rejectWithValue }) => {
    try {
        // Reuse the existing refresh thunk
        const refreshResult = await dispatch(refreshReportThunk(reportId)).unwrap();
        // If sync, we already have the fresh data — just refetch full
        // doc so ads + projections are populated.
        const full = await dispatch(fetchReportById(reportId)).unwrap();
        return full;
    }
    catch (err) {
        return rejectWithValue(typeof err === 'string' ? err : err?.message || 'Refresh failed');
    }
});
export const generateDeckThunk = createAsyncThunk('metaAdAudit/generateDeck', async ({ reportId, regenerate = false }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await metaAdAuditService.generateDeck(reportId, token || '', regenerate);
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(body?.message || 'Failed to generate deck');
        }
        return {
            reportId,
            deck: body.data.deck,
            cached: !!body.data.cached,
        };
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to generate deck');
    }
});
// ══════════════════════════════════════════════════════════════════════════
// SLICE
// ══════════════════════════════════════════════════════════════════════════
const initialState = {
    reports: [],
    total: 0,
    page: 1,
    limit: 50,
    loading: false,
    error: null,
    lastUpdated: null,
    saving: false,
    deleting: false,
    refreshing: null,
    visibleClients: [],
    visibleClientsLoading: false,
    clientsWithReports: [],
    clientsWithReportsLoading: false,
    currentReport: null,
    currentReportLoading: false,
    currentReportError: null,
    generatingDeckReportId: null,
};
const metaAdAuditSlice = createSlice({
    name: 'metaAdAudit',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetReports: (state) => {
            state.reports = [];
            state.total = 0;
            state.page = 1;
        },
        // ✅ YE ADD KARNA HAI
        clearCurrentReport: (state) => {
            state.currentReport = null;
            state.currentReportError = null;
        },
    },
    extraReducers: (builder) => {
        // fetchReports
        builder
            .addCase(fetchReports.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchReports.fulfilled, (state, action) => {
            state.loading = false;
            state.reports = action.payload.items;
            state.total = action.payload.total;
            state.page = action.payload.page;
            state.limit = action.payload.limit;
            const latest = action.payload.items.reduce((max, r) => !max || new Date(r.updated_at) > new Date(max)
                ? r.updated_at
                : max, null);
            state.lastUpdated = latest;
        })
            .addCase(fetchReports.rejected, (state, action) => {
            state.loading = false;
            state.error =
                action.payload || action.error.message || 'Load failed';
        });
        // createReport
        builder
            .addCase(createReportThunk.pending, (state) => {
            state.saving = true;
        })
            .addCase(createReportThunk.fulfilled, (state) => {
            state.saving = false;
        })
            .addCase(createReportThunk.rejected, (state, action) => {
            state.saving = false;
            state.error =
                action.payload || action.error.message || 'Create failed';
        });
        // updateReport
        builder
            .addCase(updateReportThunk.pending, (state) => {
            state.saving = true;
        })
            .addCase(updateReportThunk.fulfilled, (state) => {
            state.saving = false;
        })
            .addCase(updateReportThunk.rejected, (state, action) => {
            state.saving = false;
            state.error =
                action.payload || action.error.message || 'Update failed';
        });
        // fetchReportById
        builder
            .addCase(fetchReportById.pending, (state) => {
            state.currentReportLoading = true;
            state.currentReportError = null;
        })
            .addCase(fetchReportById.fulfilled, (state, action) => {
            state.currentReportLoading = false;
            state.currentReport = action.payload;
        })
            .addCase(fetchReportById.rejected, (state, action) => {
            state.currentReportLoading = false;
            state.currentReportError =
                action.payload ||
                    action.error.message ||
                    'Load failed';
        });
        // refreshCurrentReport (uses the refresh thunk under the hood)
        builder
            .addCase(refreshCurrentReport.pending, (state) => {
            state.currentReportLoading = true;
            state.currentReportError = null;
        })
            .addCase(refreshCurrentReport.fulfilled, (state, action) => {
            state.currentReportLoading = false;
            state.currentReport = action.payload;
        })
            .addCase(refreshCurrentReport.rejected, (state, action) => {
            state.currentReportLoading = false;
            state.currentReportError =
                action.payload ||
                    action.error.message ||
                    'Refresh failed';
        });
        // deleteReport
        builder
            .addCase(deleteReportThunk.pending, (state) => {
            state.deleting = true;
        })
            .addCase(deleteReportThunk.fulfilled, (state, action) => {
            state.deleting = false;
            // Optimistic-style cleanup; caller typically re-fetches anyway
            state.reports = state.reports.filter((r) => r.report_id !== action.payload.reportId);
            state.total = Math.max(0, state.total - 1);
        })
            .addCase(deleteReportThunk.rejected, (state, action) => {
            state.deleting = false;
            state.error =
                action.payload || action.error.message || 'Delete failed';
        });
        // refreshReport
        builder
            .addCase(refreshReportThunk.pending, (state, action) => {
            state.refreshing = action.meta.arg;
        })
            .addCase(refreshReportThunk.fulfilled, (state, action) => {
            state.refreshing = null;
            // If sync path, patch the report in list
            if (!action.payload.queued && action.payload.report) {
                const idx = state.reports.findIndex((r) => r.report_id === action.payload.reportId);
                if (idx !== -1) {
                    state.reports[idx] = action.payload.report;
                }
            }
        })
            .addCase(refreshReportThunk.rejected, (state, action) => {
            state.refreshing = null;
            state.error =
                action.payload || action.error.message || 'Refresh failed';
        });
        // fetchJobStatus — patches job state on the specific report
        builder.addCase(fetchJobStatus.fulfilled, (state, action) => {
            const idx = state.reports.findIndex((r) => r.report_id === action.payload.reportId);
            if (idx !== -1) {
                state.reports[idx].job = action.payload.job;
                if (typeof action.payload.ads_count === 'number') {
                    state.reports[idx].ads_count = action.payload.ads_count;
                }
                if (action.payload.counts) {
                    state.reports[idx].counts = action.payload.counts;
                }
                if (action.payload.updated_at) {
                    state.reports[idx].updated_at = action.payload.updated_at;
                }
                if (typeof action.payload.benchmark_window_days === 'number') {
                    state.reports[idx].benchmark_window_days = action.payload.benchmark_window_days;
                }
            }
        });
        // visible clients
        builder
            .addCase(fetchVisibleClients.pending, (state) => {
            state.visibleClientsLoading = true;
        })
            .addCase(fetchVisibleClients.fulfilled, (state, action) => {
            state.visibleClientsLoading = false;
            state.visibleClients = action.payload;
        })
            .addCase(fetchVisibleClients.rejected, (state) => {
            state.visibleClientsLoading = false;
        });
        // clients-with-reports
        builder
            .addCase(fetchClientsWithReports.pending, (state) => {
            state.clientsWithReportsLoading = true;
        })
            .addCase(fetchClientsWithReports.fulfilled, (state, action) => {
            state.clientsWithReportsLoading = false;
            state.clientsWithReports = action.payload;
        })
            .addCase(fetchClientsWithReports.rejected, (state) => {
            state.clientsWithReportsLoading = false;
        });
        builder
            .addCase(generateDeckThunk.pending, (state, action) => {
            state.generatingDeckReportId = action.meta.arg.reportId;
        })
            .addCase(generateDeckThunk.fulfilled, (state, action) => {
            state.generatingDeckReportId = null;
            // Patch the list row if it's in memory
            const idx = state.reports.findIndex((r) => r.report_id === action.payload.reportId);
            if (idx !== -1) {
                state.reports[idx].deck = action.payload.deck;
            }
            // Patch currentReport if it's the one being viewed
            if (state.currentReport &&
                state.currentReport.report_id === action.payload.reportId) {
                state.currentReport.deck = action.payload.deck;
            }
        })
            .addCase(generateDeckThunk.rejected, (state) => {
            state.generatingDeckReportId = null;
        });
    },
});
export const { clearError, resetReports, clearCurrentReport } = metaAdAuditSlice.actions;
// ══════════════════════════════════════════════════════════════════════════
// SELECTORS
// ══════════════════════════════════════════════════════════════════════════
export const selectReports = (s) => s.metaAdAudit?.reports || [];
export const selectReportsLoading = (s) => !!s.metaAdAudit?.loading;
export const selectReportsTotal = (s) => s.metaAdAudit?.total || 0;
export const selectReportsLastUpdated = (s) => s.metaAdAudit?.lastUpdated || null;
export const selectSaving = (s) => !!s.metaAdAudit?.saving;
export const selectDeleting = (s) => !!s.metaAdAudit?.deleting;
export const selectRefreshingId = (s) => s.metaAdAudit?.refreshing || null;
export const selectVisibleClients = (s) => s.metaAdAudit?.visibleClients || [];
export const selectVisibleClientsLoading = (s) => !!s.metaAdAudit?.visibleClientsLoading;
export const selectClientsWithReports = (s) => s.metaAdAudit?.clientsWithReports || [];
export const selectClientsWithReportsLoading = (s) => !!s.metaAdAudit?.clientsWithReportsLoading;
export const selectCurrentReport = (s) => s.metaAdAudit?.currentReport || null;
export const selectCurrentReportLoading = (s) => !!s.metaAdAudit?.currentReportLoading;
export const selectCurrentReportError = (s) => s.metaAdAudit?.currentReportError || null;
export const selectGeneratingDeckReportId = (s) => s.metaAdAudit?.generatingDeckReportId || null;
export default metaAdAuditSlice.reducer;
